"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Mail,
  Send,
  Sparkles,
  Clock,
  BookMarked,
  CheckCircle2,
  Send as PaperPlane,
  Crown,
  ArrowRight,
  Heart,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { Button } from "@/components/ui/button";
import { RelatedPages } from "@/components/ui/related-pages";
import { NewsletterForm } from "@/components/ui/forms";
import { BentoBlock } from "@/lib/blocks/bento-block";
import {
  Activity,
  Eye,
  Brain,
  Lightbulb,
  Target,
  ShieldCheck,
  Compass,
  Hourglass,
  Stethoscope,
  Quote,
  Mic,
} from "lucide-react";

const SAMPLE_ISSUES = [
  { num: "Issue 47", title: "Diagnose Before Treating", body: "The clinical method as the foundation of every operating decision. How differential diagnosis prevents organizational misdiagnosis.", date: "Biweekly" },
  { num: "Issue 46", title: "Triage Under Pressure", body: "Sequencing the work when everything is urgent and the room is reading the most senior person in it. The skill that determines outcomes.", date: "Biweekly" },
  { num: "Issue 45", title: "The 70% Operating Model", body: "Why the talent the world overlooks produces the most reliable operational outcomes. A decade of evidence.", date: "Biweekly" },
  { num: "Issue 44", title: "Run the Protocol; Keep the Judgment", body: "Protocols carry through the ninety-five percent of cases. Judgment handles the five percent that don't. Both are non-negotiable.", date: "Biweekly" },
  { num: "Issue 43", title: "Do No Harm", body: "The oldest principle in medicine. Under pressure, most leaders make the situation worse. The discipline of not making it worse.", date: "Biweekly" },
  { num: "Issue 42", title: "Composure as Infrastructure", body: "The senior person's state determines the team's state. Composure is not emotional intelligence — it is operational infrastructure.", date: "Biweekly" },
];

const COVERAGE = [
  "Operational Excellence",
  "Leadership as Diagnosis",
  "Women in Operations",
  "Governance Transformation",
  "Crisis Management",
  "Building in the Dark",
  "The Triple Threat",
  "Strategic Intelligence",
  "Talent Strategy",
];

const PILLARS = [
  { icon: BookOpen, title: "One Principle Per Issue", body: "Not a blog. Not a newsletter. One operating principle per issue, stated with medical-grade precision." },
  { icon: Sparkles, title: "Drawn From Practice", body: "Every principle has been tested under sovereign-grade conditions across eighteen countries and the full spectrum of operational pressure." },
  { icon: Heart, title: "Made Usable", body: "Stated in terms that any organization can implement — from startups to sovereign institutions. No abstractions." },
  { icon: BookMarked, title: "Biweekly Cadence", body: "Every fourteen days. Without exception. Discipline in the publication mirrors discipline in the operating method." },
];

export default function OperatorsNotebookPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="The Operator's Notebook"
        overlineClassName="text-amber-gold"
        title={
          <>
            One Principle. <span className="text-gradient-premium">Every Two Weeks.</span>
          </>
        }
        subtitle="The biweekly publication of Dr. Jyoti Kush — operating principles drawn from a decade of sovereign-scale operations, made applicable to any organization."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "The Operator's Notebook" }]}
        badge="Biweekly · Free · Clinical-grade"
      />

      <Section background="default" className="py-12 lg:py-16">
        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10 items-start">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-amber-gold border-amber-gold/20 mb-5">
              <BookOpen className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">What This Is</span>
            </div>
            <h2 className="text-display-md mb-5">A clinical publication for operators.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The Operator&apos;s Notebook is named for the discipline of clinical medicine — where operators (physicians, surgeons, clinicians) maintain notebooks of protocols, observations, and principles that guide their practice. The notebook is not theory; it is the accumulated operational wisdom of practice.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Each issue contains one principle — the kind that governs the operation of sixty-plus services and cross-continental teams — stated in terms that any organization can implement.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The writing follows the same standards that govern everything Dr. Jyoti Kush produces: third person, evidence-based, precise, and drawn from verified operational experience.
            </p>
          </div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:sticky lg:top-24 glass-strong rounded-2xl p-6 gradient-border"
          >
            <div className="text-overline text-amber-gold mb-2">Subscribe</div>
            <h3 className="text-display-sm mb-1">The Operator&apos;s Notebook</h3>
            <p className="text-caption text-muted-foreground mb-5">Free. Biweekly. No filler.</p>

            <div className="mb-5">
              <NewsletterForm accent="amber-gold" />
            </div>

            <div className="pt-5 border-t border-border/30 space-y-2.5">
              {["One principle per issue", "Biweekly, no exceptions", "Free, no spam, no ads"].map((b) => (
                <div key={b} className="flex items-center gap-2 text-caption text-muted-foreground">
                  <CheckCircle2 className="size-3.5 text-amber-gold" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-purple border-purple/20 mb-4">
            <PaperPlane className="size-3.5 text-purple" />
            <span className="text-overline text-purple">What Subscribers Receive</span>
          </div>
          <h2 className="text-display-md mb-4">Four things. Every issue. Without exception.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card rounded-2xl p-5 bg-gradient-to-br from-purple/10 to-transparent border border-purple/20"
            >
              <p.icon className="size-7 text-purple mb-3" />
              <h3 className="text-heading-md mb-2">{p.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
            <BookMarked className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Recent Issues</span>
          </div>
          <h2 className="text-display-md mb-4">From the archive.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six recent issues. Each is one operating principle, drawn from practice, stated with precision.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {SAMPLE_ISSUES.map((iss, i) => (
            <motion.div
              key={iss.num}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
            >
              <Link href="/insights/" className="block h-full group">
                <div className="glass-card rounded-2xl p-5 h-full bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20 hover:scale-[1.01] transition-all">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-caption font-bold text-amber-gold tracking-wider uppercase">{iss.num}</span>
                    <span className="text-caption text-muted-foreground">{iss.date}</span>
                  </div>
                  <h3 className="text-heading-md mb-2 group-hover:text-amber-gold transition-colors">{iss.title}</h3>
                  <p className="text-caption text-muted-foreground leading-relaxed mb-3">{iss.body}</p>
                  <div className="inline-flex items-center gap-1.5 text-caption font-semibold text-amber-gold group-hover:gap-2.5 transition-all">
                    Read <ArrowRight className="size-3" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-purple border-purple/20 mb-4">
            <Quote className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Featured Insights</span>
          </div>
          <h2 className="text-display-md mb-4">Excerpts from the archive.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each issue delivers one principle, stated with clinical precision, drawn from a decade of sovereign-scale operations.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: "Excellence Is a System, Not a Value", body: "If the operating steps cannot be written down, the outcome is luck, not excellence. What medicine systematized because lives depended on it, organizations must systematize because outcomes demand it." },
            { title: "Diagnose Before Treating", body: "The first rule of medicine, the most violated rule of leadership. Most organizations act on the symptom. The disease progresses undetected." },
            { title: "Own the Outcome, Not the Effort", body: "Medicine does not credit a physician for how hard the attempt was. The patient lived or did not. Organizations have lost this accountability. Restore it." },
            { title: "Hire the Talent Others Overlook", body: "The most reliable competitive advantage available is the one the market is mis-pricing. When selection optimizes for the right signals, the talent pool that emerges is the one everyone else is overlooking." },
            { title: "Build in the Dark", body: "Publicity is not impact. The things that endure are built without an audience. A decade of operating in discretion has proven this. The most durable organizations are built in the dark." },
            { title: "The Operating System Is the Culture", body: "Culture is not what organizations say. Culture is what organizations do — consistently, repeatedly, under pressure. The operating system is the culture. Change the operating steps, and the culture changes." },
          ].map((ex, i) => (
            <motion.div
              key={ex.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className="glass-card rounded-2xl p-5 bg-gradient-to-br from-purple/10 to-transparent border border-purple/20 relative"
            >
              <Quote className="absolute top-3 right-3 size-4 text-purple/30" />
              <h3 className="text-heading-sm mb-2 text-purple pr-6">{ex.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed italic">&ldquo;{ex.body}&rdquo;</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <BentoBlock
          eyebrow="Topics Covered"
          title="The full operating spectrum."
          description="Eight to ten topic domains drawn from a decade of sovereign-scale operations — the disciplines that govern the operating method. Each domain is a domain of the practice, not a content category."
          items={[
            {
              icon: <Activity className="size-5" />,
              title: "Operational Excellence",
              body: "Why values statements fail where systems succeed. The four components of an operating system for excellence — protocols, checklists, reviews, accountability.",
              accent: "amber-gold",
              span: "lg",
            },
            {
              icon: <Stethoscope className="size-5" />,
              title: "The Clinical Method",
              body: "Differential diagnosis for organizations. Triage under pressure. Why do-no-harm is the most underrated leadership principle in business.",
              accent: "amber-gold",
              span: "md",
            },
            {
              icon: <Crown className="size-5" />,
              title: "The 70% Operating Model",
              body: "Why majority-female teams outperform — not as a social proposition, but as an operational finding with a decade of evidence.",
              accent: "amber-gold",
              span: "md",
            },
            {
              icon: <Eye className="size-5" />,
              title: "Building Things That Last",
              body: "Why publicity is not impact. The discipline of building in the dark — why discretion is the operating advantage most organizations forfeit.",
              accent: "amber-gold",
              span: "sm",
            },
            {
              icon: <Compass className="size-5" />,
              title: "Governance & Leadership",
              body: "Why governance wins elections — the operating system for sovereign-scale leadership. What governments can learn from clinical medicine.",
              accent: "amber-gold",
              span: "sm",
            },
            {
              icon: <Brain className="size-5" />,
              title: "The Intersection",
              body: "How the medical method applies to technology operations. Why political-grade stakes demand clinical-grade discipline. The triple threat as one operating method.",
              accent: "amber-gold",
              span: "tall",
            },
            {
              icon: <Target className="size-5" />,
              title: "Talent & Team Building",
              body: "Why the most valuable talent pool is the one the market is mispricing. The five signals that predict operational performance better than credentials.",
              accent: "amber-gold",
              span: "sm",
            },
            {
              icon: <ShieldCheck className="size-5" />,
              title: "Decision-Making Under Pressure",
              body: "The clinical framework for high-stakes decisions with incomplete information. Why speed and accuracy are complementary forces when the diagnostic process is disciplined.",
              accent: "amber-gold",
              span: "sm",
            },
            {
              icon: <Hourglass className="size-5" />,
              title: "The Compounding Effect",
              body: "Why one principle at a time produces extraordinary results. The architecture of operational knowledge built through disciplined absorption over years.",
              accent: "amber-gold",
              span: "wide",
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-cyan border-cyan/20 mb-4">
            <Lightbulb className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">The Operator&apos;s Method</span>
          </div>
          <h2 className="text-display-md mb-4">How every principle is produced.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The clinical discipline that governs each issue: observe, diagnose, prescribe, monitor. Evidence-based, protocol-driven, outcome-accountable.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { phase: "01", title: "Observe", body: "The principle is identified through operational observation — a pattern seen across teams, platforms, engagements, or countries. The disciplined, systematic collection of evidence from operational practice." },
            { phase: "02", title: "Diagnose", body: "The principle is analyzed — why does it work? What is the mechanism? What is the evidence? Tested against alternatives, validated through clinical reasoning." },
            { phase: "03", title: "Prescribe", body: "The principle is stated clearly and made applicable — what should the reader do? The action, the context, the measurement. No room for misinterpretation." },
            { phase: "04", title: "Monitor", body: "The principle is tested — does it hold across contexts, scales, organizations? Ongoing validation through application. The clinical method as quality control." },
          ].map((s, i) => (
            <motion.div
              key={s.phase}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.4, delay: i * 0.06 }}
              className="glass-card rounded-2xl p-5 bg-gradient-to-br from-cyan/10 to-transparent border border-cyan/20"
            >
              <div className="text-display-sm font-bold text-cyan/40 leading-none mb-3">{s.phase}</div>
              <h3 className="text-heading-md mb-2 text-cyan">{s.title}</h3>
              <p className="text-caption text-muted-foreground leading-relaxed">{s.body}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-tinted-emerald border-emerald/20 mb-4">
              <Mic className="size-3.5 text-emerald" />
              <span className="text-overline text-emerald">Ideal Readers</span>
            </div>
            <h2 className="text-display-md mb-4">Who benefits from the notebook.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-4">
              The Operator&apos;s Notebook is built for leaders who recognize that operational excellence is not an aspiration but a system — and who want the principles that build it. Six reader profiles benefit most.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Each profile finds something different in the publication. The common thread: principles grounded in sovereign-scale evidence, immediately applicable, and compounding in value over time.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { icon: Crown, title: "Executives and Leaders", body: "Operating at senior levels in organizations that demand operational excellence. Need principles that can be implemented this week, not studied over months." },
              { icon: Lightbulb, title: "Founders and Entrepreneurs", body: "Building organizations from the ground up and seeking the operating discipline that produces verified results — from the first hire to the thousandth." },
              { icon: Heart, title: "Women in Leadership", body: "Seeking a role model and operational authority who has built, not just advocated. Evidence from practice, not values statements." },
              { icon: Activity, title: "Operations Professionals", body: "Practitioners who need the advanced principles that govern high-stakes operations — diagnostic frameworks, monitoring protocols, sustained performance." },
              { icon: Compass, title: "Governance and Policy Leaders", body: "Seeking the operating method that converts authority into outcomes. Government officials, policy architects, institutional leaders." },
              { icon: Sparkles, title: "Aspiring Leaders", body: "Preparing for leadership roles and seeking the clinical discipline that governs high-performance organizations. Principles internalized early, applied throughout careers." },
            ].map((r, i) => (
              <motion.div
                key={r.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="glass-card rounded-xl p-4 bg-gradient-to-br from-emerald/10 to-transparent border border-emerald/20"
              >
                <r.icon className="size-5 text-emerald mb-2" />
                <h3 className="text-heading-sm mb-1.5">{r.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{r.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-12 lg:py-16">
        <div className="text-center mb-10">
          <h2 className="text-display-sm mb-3">Topic coverage</h2>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2">
          {COVERAGE.map((c) => (
            <span
              key={c}
              className="glass rounded-full px-4 py-2 text-caption font-semibold hover:scale-105 transition-transform"
            >
              <span className="text-amber-gold">·</span>{" "}
              <span className="text-muted-foreground ml-1">{c}</span>
            </span>
          ))}
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute inset-0 -m-10 rounded-3xl bg-gradient-premium opacity-[0.06] blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -top-20 -right-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -left-20 w-96 h-96 rounded-full bg-red/15 blur-3xl pointer-events-none" />

          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Crown className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-4">The signal, not the noise.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The gap between knowing what excellence looks like and knowing how to build it is vast — and most of the content produced to bridge that gap is noise. The Operator&apos;s Notebook is the signal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button asChild size="lg" className="h-12 px-6 bg-gradient-premium shadow-lg">
                <Link href="/insights/">
                  <Mail className="size-4" />
                  Subscribe
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 px-6 glass">
                <Link href="/insights/">
                  <BookOpen className="size-4" />
                  Read the Archive
                </Link>
              </Button>
            </div>
            <div className="mt-8 inline-flex items-center gap-2 text-caption text-muted-foreground">
              <Clock className="size-3" />
              <span>One principle. Every two weeks. Without exception.</span>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Insights", description: "The full archive of essays on operational excellence.", href: "/insights/", icon: BookOpen, accent: "amber-gold" },
          { title: "The 70% Project", description: "The operating case for women in leadership.", href: "/the-70-percent-project/", icon: Heart, accent: "red" },
          { title: "Speaking Topics", description: "Six signature keynotes on how excellent things run.", href: "/speaking/", icon: Sparkles, accent: "purple" },
          { title: "About Dr. Jyoti Kush", description: "The physician who became an operator.", href: "/about/", icon: Crown, accent: "cyan" },
        ]}
      />
    </>
  );
}
